import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  goalCode: '',
  displayName: '',
  goalText: '',
  shortTermEnabled: true,
  longTermEnabled: true,
  shortTermDefaultValue: '',
  longTermDefaultValue: '',
  valueUnit: '',
  minimumAge: '',
  maximumAge: '',
  allowPatientSpecificEdit: false,
  required: true,
  unchangedNoteLimit: '',
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    goalCode: template?.goalCode || '',
    displayName: template?.displayName || '',
    goalText: template?.goalText || '',
    shortTermEnabled: Boolean(template?.shortTermEnabled),
    longTermEnabled: Boolean(template?.longTermEnabled),
    shortTermDefaultValue: template?.shortTermDefaultValue || '',
    longTermDefaultValue: template?.longTermDefaultValue || '',
    valueUnit: template?.valueUnit || '',
    minimumAge:
      template?.minimumAge === null || template?.minimumAge === undefined
        ? ''
        : String(template.minimumAge),
    maximumAge:
      template?.maximumAge === null || template?.maximumAge === undefined
        ? ''
        : String(template.maximumAge),
    allowPatientSpecificEdit: Boolean(template?.allowPatientSpecificEdit),
    required: Boolean(template?.required),
    unchangedNoteLimit:
      template?.unchangedNoteLimit === null ||
      template?.unchangedNoteLimit === undefined
        ? ''
        : String(template.unchangedNoteLimit),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function toOptionalInteger(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  return Number(value);
}

function validateOptionalNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '';
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateRequiredNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function formatAgeRange(minimumAge, maximumAge) {
  const minimum =
    minimumAge === null || minimumAge === undefined
      ? null
      : Number(minimumAge);
  const maximum =
    maximumAge === null || maximumAge === undefined
      ? null
      : Number(maximumAge);

  if (minimum === null && maximum === null) {
    return 'All ages';
  }

  if (minimum !== null && maximum !== null) {
    return `Ages ${minimum}-${maximum}`;
  }

  if (minimum !== null) {
    return `Age ${minimum}+`;
  }

  return `Age ${maximum} or younger`;
}

function validateForm(form, bodyPartMap) {
  const errors = {};
  const bodyPartTemplateId = Number(form.bodyPartTemplateId);
  const bodyPart = bodyPartMap.get(bodyPartTemplateId);
  const goalCode = String(form.goalCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const goalText = String(form.goalText || '').trim();
  const shortTermDefaultValue = String(form.shortTermDefaultValue || '').trim();
  const longTermDefaultValue = String(form.longTermDefaultValue || '').trim();
  const valueUnit = String(form.valueUnit || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
    errors.bodyPartTemplateId = 'Select a body part.';
  }

  if (!goalCode) {
    errors.goalCode = 'Goal code is required.';
  } else if (goalCode.length > 120) {
    errors.goalCode = 'Goal code cannot exceed 120 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(goalCode)) {
    errors.goalCode =
      'Goal code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Goal display name is required.';
  } else if (displayName.length > 150) {
    errors.displayName = 'Goal display name cannot exceed 150 characters.';
  }

  if (!goalText) {
    errors.goalText = 'Goal text is required.';
  } else if (goalText.length > 1500) {
    errors.goalText = 'Goal text cannot exceed 1500 characters.';
  }

  if (!form.shortTermEnabled && !form.longTermEnabled) {
    errors.goalTypes =
      'Enable Short-Term Goal, Long-Term Goal, or both for this template.';
  }

  if (shortTermDefaultValue.length > 100) {
    errors.shortTermDefaultValue =
      'Short-term default value cannot exceed 100 characters.';
  }

  if (longTermDefaultValue.length > 100) {
    errors.longTermDefaultValue =
      'Long-term default value cannot exceed 100 characters.';
  }

  if (valueUnit.length > 50) {
    errors.valueUnit = 'Goal value unit cannot exceed 50 characters.';
  }

  const minimumAgeError = validateOptionalNonNegativeInteger(
    form.minimumAge,
    'Minimum age must be a whole number of 0 or greater.'
  );
  if (minimumAgeError) {
    errors.minimumAge = minimumAgeError;
  }

  const maximumAgeError = validateOptionalNonNegativeInteger(
    form.maximumAge,
    'Maximum age must be a whole number of 0 or greater.'
  );
  if (maximumAgeError) {
    errors.maximumAge = maximumAgeError;
  }

  if (!minimumAgeError && !maximumAgeError) {
    const minimumAge = toOptionalInteger(form.minimumAge);
    const maximumAge = toOptionalInteger(form.maximumAge);

    if (
      minimumAge !== null &&
      maximumAge !== null &&
      minimumAge > maximumAge
    ) {
      errors.maximumAge = 'Maximum age cannot be less than minimum age.';
    }
  }

  const unchangedError = validateRequiredNonNegativeInteger(
    form.unchangedNoteLimit,
    'Unchanged-note limit must be a whole number of 0 or greater.'
  );
  if (unchangedError) {
    errors.unchangedNoteLimit = unchangedError;
  }

  const displayOrderError = validateRequiredNonNegativeInteger(
    form.displayOrder,
    'Display order must be a whole number of 0 or greater.'
  );
  if (displayOrderError) {
    errors.displayOrder = displayOrderError;
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  if (form.active && bodyPart && !bodyPart.active) {
    errors.active =
      'An active goal template cannot belong to an inactive body part.';
  }

  return errors;
}

function buildRequest(form) {
  return {
    bodyPartTemplateId: Number(form.bodyPartTemplateId),
    goalCode: String(form.goalCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    goalText: String(form.goalText || '').trim(),
    shortTermEnabled: Boolean(form.shortTermEnabled),
    longTermEnabled: Boolean(form.longTermEnabled),
    shortTermDefaultValue: form.shortTermEnabled
      ? String(form.shortTermDefaultValue || '').trim() || null
      : null,
    longTermDefaultValue: form.longTermEnabled
      ? String(form.longTermDefaultValue || '').trim() || null
      : null,
    valueUnit: String(form.valueUnit || '').trim() || null,
    minimumAge: toOptionalInteger(form.minimumAge),
    maximumAge: toOptionalInteger(form.maximumAge),
    allowPatientSpecificEdit: Boolean(form.allowPatientSpecificEdit),
    required: Boolean(form.required),
    unchangedNoteLimit: Number(form.unchangedNoteLimit),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: String(form.adminNotes || '').trim() || null,
  };
}

export default function GoalTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [selectedBodyPartId, setSelectedBodyPartId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [goalTypeFilter, setGoalTypeFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.goalTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const selectedBodyPart = useMemo(
    () => bodyPartMap.get(Number(selectedBodyPartId)) || null,
    [bodyPartMap, selectedBodyPartId]
  );

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (goalTypeFilter === 'STG' && !template.shortTermEnabled) {
          return false;
        }

        if (goalTypeFilter === 'LTG' && !template.longTermEnabled) {
          return false;
        }

        if (goalTypeFilter === 'BOTH') {
          if (!template.shortTermEnabled || !template.longTermEnabled) {
            return false;
          }
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          template.goalTemplateId,
          template.goalCode,
          template.displayName,
          template.goalText,
          template.valueUnit,
          template.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, searchTerm, statusFilter, goalTypeFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  useEffect(() => {
    loadBodyParts();
  }, []);

  useEffect(() => {
    if (selectedBodyPartId) {
      loadTemplates(selectedBodyPartId);
    } else {
      setTemplates([]);
      setLoading(false);
    }
  }, [selectedBodyPartId]);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadBodyParts = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const bodyPartData =
        await clinicalTemplateAdminService.getAllBodyPartTemplates();
      const normalized = Array.isArray(bodyPartData) ? bodyPartData : [];

      setBodyParts(normalized);

      if (normalized.length > 0) {
        const firstActive = normalized.find((bodyPart) => bodyPart.active);
        const initial = firstActive || normalized[0];

        setSelectedBodyPartId((current) =>
          current || String(initial.bodyPartTemplateId)
        );

        setForm((current) => ({
          ...current,
          bodyPartTemplateId:
            current.bodyPartTemplateId || String(initial.bodyPartTemplateId),
        }));
      } else {
        setLoading(false);
      }
    } catch (error) {
      setBodyParts([]);
      setTemplates([]);
      setLoadError(
        error?.message || 'Body-part configuration could not be loaded.'
      );
      setLoading(false);
    }
  };

  const loadTemplates = async (bodyPartTemplateId) => {
    try {
      setLoading(true);
      setLoadError('');

      const templateData =
        await clinicalTemplateAdminService.getAllGoalTemplates(
          bodyPartTemplateId
        );

      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      setTemplates([]);
      setLoadError(
        error?.message || 'Goal-template configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({
      ...EMPTY_FORM,
      bodyPartTemplateId: selectedBodyPartId || '',
    });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setSelectedBodyPartId(String(template.bodyPartTemplateId));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      const next = { ...current };
      delete next[field];

      if (field === 'shortTermEnabled' || field === 'longTermEnabled') {
        delete next.goalTypes;
      }

      return next;
    });
  };

  const handleFormBodyPartChange = (value) => {
    const bodyPart = bodyPartMap.get(Number(value));

    setForm((current) => ({
      ...current,
      bodyPartTemplateId: value,
      active: current.active && bodyPart && !bodyPart.active ? false : current.active,
    }));

    setSelectedBodyPartId(value);
    setFormErrors({});
    setActionError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form, bodyPartMap);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateGoalTemplate(
          editingTemplate.goalTemplateId,
          request
        );
        showSuccess('Goal template updated.');
      } else {
        await clinicalTemplateAdminService.createGoalTemplate(request);
        showSuccess('Goal template created.');
      }

      const bodyPartId = String(request.bodyPartTemplateId);
      setSelectedBodyPartId(bodyPartId);
      setEditingTemplate(null);
      setForm({
        ...EMPTY_FORM,
        bodyPartTemplateId: bodyPartId,
      });
      setFormErrors({});
      setActionError('');
      await loadTemplates(bodyPartId);
    } catch (error) {
      setActionError(error?.message || 'Goal template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;
    const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

    if (nextActive && bodyPart && !bodyPart.active) {
      setActionError(
        `"${bodyPart.displayName}" is inactive. Activate the body part before activating this goal template.`
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Historical clinical notes will keep their existing goal template reference.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.goalTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setGoalTemplateActive(
        template.goalTemplateId,
        nextActive
      );

      showSuccess(`Goal template ${nextActive ? 'activated' : 'deactivated'}.`);

      if (editingTemplate?.goalTemplateId === template.goalTemplateId) {
        setForm((current) => ({
          ...current,
          active: nextActive,
        }));
        setEditingTemplate((current) => ({
          ...current,
          active: nextActive,
        }));
      }

      await loadTemplates(template.bodyPartTemplateId);
    } catch (error) {
      setActionError(
        error?.message ||
          `Goal template could not be ${nextActive ? 'activated' : 'deactivated'}.`
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="goal-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">GOAL CONFIGURATION</span>
            <h2>{isEditing ? 'Edit Goal Template' : 'Create Goal Template'}</h2>
            <p>
              Configure reusable clinical goal wording, STG/LTG behavior,
              patient age restrictions, default values, and final-note
              requirements for one body part.
            </p>
          </div>

          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {actionError && (
          <div className="clinical-template-message error" role="alert">
            {actionError}
          </div>
        )}

        {success && (
          <div className="clinical-template-message success" role="status">
            {success}
          </div>
        )}

        <form className="goal-template-form" onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body Part
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  handleFormBodyPartChange(event.target.value)
                }
                disabled={isEditing || saving}
              >
                <option value="">Select body part</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
              {isEditing && (
                <small>Body-part assignment cannot be changed after creation.</small>
              )}
            </label>

            <label>
              Goal Code
              <input
                type="text"
                value={form.goalCode}
                onChange={(event) =>
                  updateFormField('goalCode', event.target.value.toUpperCase())
                }
                maxLength={120}
                disabled={isEditing || saving}
                placeholder="WALKING_TOLERANCE"
              />
              {formErrors.goalCode && (
                <span className="clinical-template-field-error">
                  {formErrors.goalCode}
                </span>
              )}
              {isEditing && (
                <small>Goal code cannot be changed after creation.</small>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={150}
                disabled={saving}
                placeholder="Walking Tolerance"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Value Unit
              <input
                type="text"
                value={form.valueUnit}
                onChange={(event) =>
                  updateFormField('valueUnit', event.target.value)
                }
                maxLength={50}
                disabled={saving}
                placeholder="blocks, minutes, degrees, pain..."
              />
              {formErrors.valueUnit && (
                <span className="clinical-template-field-error">
                  {formErrors.valueUnit}
                </span>
              )}
            </label>

            <label>
              Minimum Patient Age
              <input
                type="number"
                min="0"
                step="1"
                value={form.minimumAge}
                onChange={(event) =>
                  updateFormField('minimumAge', event.target.value)
                }
                disabled={saving}
                placeholder="No minimum"
              />
              {formErrors.minimumAge && (
                <span className="clinical-template-field-error">
                  {formErrors.minimumAge}
                </span>
              )}
            </label>

            <label>
              Maximum Patient Age
              <input
                type="number"
                min="0"
                step="1"
                value={form.maximumAge}
                onChange={(event) =>
                  updateFormField('maximumAge', event.target.value)
                }
                disabled={saving}
                placeholder="No maximum"
              />
              {formErrors.maximumAge && (
                <span className="clinical-template-field-error">
                  {formErrors.maximumAge}
                </span>
              )}
            </label>

            <label>
              Unchanged-Note Warning Limit
              <input
                type="number"
                min="0"
                step="1"
                value={form.unchangedNoteLimit}
                onChange={(event) =>
                  updateFormField('unchangedNoteLimit', event.target.value)
                }
                disabled={saving}
                placeholder="Example: 2"
              />
              {formErrors.unchangedNoteLimit && (
                <span className="clinical-template-field-error">
                  {formErrors.unchangedNoteLimit}
                </span>
              )}
              <small>0 disables the repeated-value warning.</small>
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Goal Text
            <textarea
              rows={4}
              value={form.goalText}
              onChange={(event) => updateFormField('goalText', event.target.value)}
              maxLength={1500}
              disabled={saving}
              placeholder="Patient will be able to walk for {VALUE} blocks."
            />
            {formErrors.goalText && (
              <span className="clinical-template-field-error">
                {formErrors.goalText}
              </span>
            )}
            <small>
              Use {'{VALUE}'} where the patient-specific STG/LTG target should
              appear when this goal uses a configurable value.
            </small>
          </label>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.shortTermEnabled}
                onChange={(event) => {
                  const checked = event.target.checked;
                  updateFormField('shortTermEnabled', checked);
                  if (!checked) {
                    updateFormField('shortTermDefaultValue', '');
                  }
                }}
                disabled={saving}
              />
              <span>
                <strong>Short-Term Goal</strong>
                <small>This template may be used as an STG.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.longTermEnabled}
                onChange={(event) => {
                  const checked = event.target.checked;
                  updateFormField('longTermEnabled', checked);
                  if (!checked) {
                    updateFormField('longTermDefaultValue', '');
                  }
                }}
                disabled={saving}
              />
              <span>
                <strong>Long-Term Goal</strong>
                <small>This template may be used as an LTG.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.allowPatientSpecificEdit}
                onChange={(event) =>
                  updateFormField(
                    'allowPatientSpecificEdit',
                    event.target.checked
                  )
                }
                disabled={saving}
              />
              <span>
                <strong>Allow Patient-Specific Wording</strong>
                <small>
                  Clinicians may customize the final wording for the patient.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(event) =>
                  updateFormField('required', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Required</strong>
                <small>
                  Goal normally must be completed before final-note validation.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>Available on newly created clinical notes.</small>
                {formErrors.active && (
                  <span className="clinical-template-field-error">
                    {formErrors.active}
                  </span>
                )}
              </span>
            </label>
          </div>

          {formErrors.goalTypes && (
            <div className="clinical-template-message error" role="alert">
              {formErrors.goalTypes}
            </div>
          )}

          <div className="clinical-template-form-grid">
            <label>
              Short-Term Default Value
              <input
                type="text"
                value={form.shortTermDefaultValue}
                onChange={(event) =>
                  updateFormField('shortTermDefaultValue', event.target.value)
                }
                maxLength={100}
                disabled={saving || !form.shortTermEnabled}
                placeholder={form.shortTermEnabled ? 'Optional STG value' : 'STG disabled'}
              />
              {formErrors.shortTermDefaultValue && (
                <span className="clinical-template-field-error">
                  {formErrors.shortTermDefaultValue}
                </span>
              )}
            </label>

            <label>
              Long-Term Default Value
              <input
                type="text"
                value={form.longTermDefaultValue}
                onChange={(event) =>
                  updateFormField('longTermDefaultValue', event.target.value)
                }
                maxLength={100}
                disabled={saving || !form.longTermEnabled}
                placeholder={form.longTermEnabled ? 'Optional LTG value' : 'LTG disabled'}
              />
              {formErrors.longTermDefaultValue && (
                <span className="clinical-template-field-error">
                  {formErrors.longTermDefaultValue}
                </span>
              )}
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save Goal Template'
                  : 'Create Goal Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">
              CONFIGURED GOAL TEMPLATES
            </span>
            <h2>Goal Templates</h2>
            <p>
              {selectedBodyPart
                ? `${selectedBodyPart.displayName}: ${templates.length} total · ${activeCount} active`
                : 'Select a body part to view its goal configuration.'}
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={() =>
              selectedBodyPartId && loadTemplates(selectedBodyPartId)
            }
            disabled={
              !selectedBodyPartId ||
              loading ||
              saving ||
              activeActionId !== null
            }
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        <div
          className="clinical-template-list-toolbar"
          style={{
            gridTemplateColumns:
              '240px minmax(220px, 1fr) 180px 180px',
          }}
        >
          <select
            value={selectedBodyPartId}
            onChange={(event) => {
              setSelectedBodyPartId(event.target.value);
              if (!isEditing) {
                setForm((current) => ({
                  ...current,
                  bodyPartTemplateId: event.target.value,
                }));
              }
              setActionError('');
            }}
          >
            <option value="">Select body part</option>
            {bodyParts.map((bodyPart) => (
              <option
                key={bodyPart.bodyPartTemplateId}
                value={bodyPart.bodyPartTemplateId}
              >
                {bodyPart.displayName} ({bodyPart.bodyPartCode})
                {!bodyPart.active ? ' - Inactive' : ''}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search goal code, wording, name, notes..."
            disabled={!selectedBodyPartId}
          />

          <select
            value={goalTypeFilter}
            onChange={(event) => setGoalTypeFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All goal types</option>
            <option value="STG">STG enabled</option>
            <option value="LTG">LTG enabled</option>
            <option value="BOTH">STG + LTG</option>
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>

        {!selectedBodyPartId ? (
          <div className="clinical-template-empty-state">
            Select a body part to manage its goal templates.
          </div>
        ) : loading ? (
          <div className="clinical-template-empty-state">
            Loading goal templates...
          </div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No goal templates match the current filter.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Goal</th>
                  <th>STG / LTG</th>
                  <th>Age</th>
                  <th>Required</th>
                  <th>Patient Edit</th>
                  <th>Unchanged Limit</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.goalTemplateId}>
                    <td>{template.displayOrder}</td>
                    <td>
                      <strong>{template.displayName}</strong>
                      <small className="clinical-template-table-subtext">
                        {template.goalCode}
                      </small>
                      <small className="clinical-template-table-subtext">
                        {template.goalText}
                      </small>
                      {(template.shortTermDefaultValue ||
                        template.longTermDefaultValue ||
                        template.valueUnit) && (
                        <small className="clinical-template-table-subtext">
                          STG: {template.shortTermDefaultValue || '—'} · LTG:{' '}
                          {template.longTermDefaultValue || '—'}
                          {template.valueUnit ? ` · ${template.valueUnit}` : ''}
                        </small>
                      )}
                      {template.adminNotes && (
                        <small className="clinical-template-table-subtext">
                          {template.adminNotes}
                        </small>
                      )}
                    </td>
                    <td>
                      {template.shortTermEnabled ? 'STG' : ''}
                      {template.shortTermEnabled && template.longTermEnabled
                        ? ' + '
                        : ''}
                      {template.longTermEnabled ? 'LTG' : ''}
                    </td>
                    <td>
                      {formatAgeRange(template.minimumAge, template.maximumAge)}
                    </td>
                    <td>{template.required ? 'Yes' : 'No'}</td>
                    <td>{template.allowPatientSpecificEdit ? 'Allowed' : 'No'}</td>
                    <td>{template.unchangedNoteLimit}</td>
                    <td>
                      <span
                        className={`clinical-template-status ${
                          template.active ? 'active' : 'inactive'
                        }`}
                      >
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {formatDateTime(template.updatedAt)}
                      <small className="clinical-template-table-subtext">
                        Staff {template.updatedByStaffId ?? 'N/A'}
                      </small>
                    </td>
                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => beginEdit(template)}
                          disabled={saving || activeActionId !== null}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-success-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={
                            saving || activeActionId === template.goalTemplateId
                          }
                        >
                          {activeActionId === template.goalTemplateId
                            ? 'Updating...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
